import React from "react";
import { proxyAvatar } from "../../../_base/helper/ImageProxy";
import {
  Box,
  Flex,
  ScrollArea,
  Text,
  Title,
  Loader,
  Avatar,
  Paper,
  Group,
  Badge,
  Textarea,
  Button,
  Divider,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { formatDateTransfer } from "../../../_base/extension/FormatDate";
import { repositorySign } from "../../../_base/_const/_constVar";
import { MessageResponse } from "../../../model/MessageResponse";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { IconMessage, IconClock, IconSend, IconTrash } from "@tabler/icons-react";

interface CommentData {
  id: number;
  entityId: number | null;
  entityType: string | null;
  status: string | null;
  createdById: number;
  mentionIdList: string | null;
  email: string | null;
  createdAt: string;
  updatedAt: string | null;
  deletedAt: string | null;
  content: string;
  setupsignid: number;
  isdelete: boolean;
  username: string;
}

interface CommentModalProps {
  documentId: number | null;
}

interface AddCommentModel {
  documentId: number;
  content: string;
  mentionUserIds?: number[]; // Thêm mảng ID user được tag
}

interface TagUser {
  id: number;
  userName: string;
  name: string;
  avatar?: string;
  isAll?: boolean;
}

// Create a separate component for the modal content
const CommentModalContent = ({ documentId }: { documentId: number }) => {
  const [comments, setComments] = React.useState<CommentData[]>([]);
  const [newComment, setNewComment] = React.useState(""); // Chỉ cần một state, lưu @id
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [tagUsers, setTagUsers] = React.useState<TagUser[]>([]);
  const [showTagDropdown, setShowTagDropdown] = React.useState(false);
  const [tagSearch, setTagSearch] = React.useState("");
  const [mentionedUsers, setMentionedUsers] = React.useState<number[]>([]); // Lưu danh sách ID user được tag
  const [rawComment, setRawComment] = React.useState(""); // Lưu dạng thô với <a>@tên</a>
  const [displayComment, setDisplayComment] = React.useState(""); // Lưu dạng hiển thị @tên
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const fetchComments = async () => {
    try {
      const response = await repositorySign.get<MessageResponse<CommentData[]>>(
        `/api/v1/SignPage/get-comment?documentId=${documentId}`
      );

      if (response && response.success) {
        // Sort comments by creation date - oldest first, newest last (like Facebook)
        const sortedComments = (response.data || []).sort((a, b) => 
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
        setComments(sortedComments);
      } else {
        NotificationExtension.Fails(
          response?.message || "Lỗi khi tải danh sách bình luận !"
        );
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      NotificationExtension.Fails("Lỗi khi tải danh sách bình luận !");
    } finally {
      setLoading(false);
    }
  };

  const addComment = async () => {
    if (!displayComment.trim()) {
      console.log("Showing info notification for empty comment");
      NotificationExtension.Info("Vui lòng nhập nội dung bình luận !");
      return;
    }

    setIsSubmitting(true);
    try {
      // Gửi nguyên displayComment có chứa thẻ <a>@tên</a>
      const addCommentData: AddCommentModel = {
        documentId: documentId,
        content: displayComment.trim(), // Gửi nguyên HTML tags
        mentionUserIds: mentionedUsers.length > 0 ? mentionedUsers : undefined,
      };

      console.log("Sending comment data:", addCommentData);
      const response = await repositorySign.post<MessageResponse<any>>(
        "/api/v1/SignPage/add-comment",
        addCommentData
      );

      console.log("Add comment response:", response);
      if (response && response.success) {
        console.log("Showing success notification");
        NotificationExtension.Success("Thêm bình luận thành công !");
        setDisplayComment("");
        setRawComment("");
        setMentionedUsers([]);
        // Reload comments
        await fetchComments();
      } else {
        console.log("Showing error notification");
        NotificationExtension.Fails(
          response?.message || "Lỗi khi thêm bình luận !"
        );
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      console.log("Showing error notification for exception");
      NotificationExtension.Fails("Lỗi khi thêm bình luận !");
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteComment = async (commentId: number) => {
    try {
      console.log("Deleting comment with ID:", commentId);
      const response = await repositorySign.get<MessageResponse<any>>(
        `/api/v1/SignPage/delete-comment-sign?id=${commentId}`
      );

      console.log("Delete comment response:", response);
      if (response && response.success) {
        console.log("Showing delete success notification");
        NotificationExtension.Success("Xóa bình luận thành công !");
        // Reload comments after successful deletion
        await fetchComments();
      } else {
        console.log("Showing delete error notification");
        NotificationExtension.Fails(
          response?.message || "Lỗi khi xóa bình luận !"
        );
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      console.log("Showing delete error notification for exception");
      NotificationExtension.Fails("Lỗi khi xóa bình luận !");
    }
  };

  const handleDeleteComment = (commentId: number) => {
    modals.openConfirmModal({
      title: "Xác nhận xóa",
      children: (
        <Text size="sm">
          Bạn có chắc chắn muốn xóa bình luận này không? Thao tác này không thể hoàn tác.
        </Text>
      ),
      labels: { confirm: "Xóa", cancel: "Hủy" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteComment(commentId),
    });
  };

  React.useEffect(() => {
    fetchComments();
    fetchTagUsers();
  }, []);

  // Fetch taggable users
  const fetchTagUsers = async () => {
    try {
      // `/api/v1/SignPage/get-user-tag-comment?documentId=${documentId}`
      const response = await repositorySign.get<MessageResponse<TagUser[]>>(
        `/api/v1/users/get-all`
      );
      if (response && response.success) {
        setTagUsers(response.data || []);
      }
    } catch (error) {
      // Optionally notify error
    }
  };

  // Handle textarea input for @
  const handleTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.currentTarget.value;
    setDisplayComment(value);
    setRawComment(value);

    // Tìm ký tự @ gần nhất trước con trỏ (chưa có dấu ngoặc đóng)
    const cursorPos = event.currentTarget.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);
    // Match @( chưa đóng hoặc @ đơn lẻ
    const match = textBeforeCursor.match(/@\(?([^)@]*)$/);
    if (match) {
      setTagSearch(match[1] || "");
      setShowTagDropdown(true);
    } else {
      setShowTagDropdown(false);
      setTagSearch("");
    }
  };

  // Insert @(tên) at cursor và lưu ID
  const handleTagUserSelect = (user: TagUser) => {
    if (!textareaRef.current) return;
    const textarea = textareaRef.current;
    const value = displayComment;
    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);
    // Match @( chưa đóng hoặc @ đơn lẻ
    const match = textBeforeCursor.match(/@\(?([^)@]*)$/);
    if (match) {
      const start = cursorPos - match[0].length;
      
      // Format tag với dấu ngoặc: @(tên)
      const tagValue = user.isAll ? "@(Báo cho cả nhóm) " : `@(${user.name}) `;
      
      // Kiểm tra xem user đã được tag chưa (trừ @All)
      if (!user.isAll) {
        const userAlreadyTagged = displayComment.includes(`@(${user.name})`);
        if (userAlreadyTagged) {
          setShowTagDropdown(false);
          setTagSearch("");
          return;
        }
      }
      
      const newValue =
        value.slice(0, start) +
        tagValue +
        value.slice(cursorPos);

      setDisplayComment(newValue);
      setRawComment(newValue);

      // Thêm user ID vào danh sách mention (nếu chưa có)
      if (!user.isAll && !mentionedUsers.includes(user.id)) {
        setMentionedUsers(prev => [...prev, user.id]);
      } else if (user.isAll) {
        const allUserIds = tagUsers.map(u => u.id);
        setMentionedUsers(allUserIds);
      }

      setShowTagDropdown(false);
      setTagSearch("");
      
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = start + tagValue.length;
      }, 0);
    }
  };

  // Khi hiển thị comment, highlight các @(tên) với màu xanh
  const renderCommentContent = (content: string) => {
    if (!content) return content;
    
    // Replace @(tên) với link màu xanh
    let processedContent = content;
    
    // Xử lý @(Báo cho cả nhóm)
    processedContent = processedContent.replace(
      /@\(Báo cho cả nhóm\)/g,
      '<span style="color: #228be6; font-weight: 500; cursor: pointer;">@(Báo cho cả nhóm)</span>'
    );
    
    // Xử lý @(tên user) - match tất cả các pattern @(...)
    processedContent = processedContent.replace(
      /@\(([^)]+)\)/g,
      '<span style="color: #228be6; font-weight: 500; cursor: pointer;">@($1)</span>'
    );
    
    return processedContent;
  };

  const currentUsername = localStorage.getItem("username") || "";
  const currentUserId = localStorage.getItem("userId") || localStorage.getItem("empId") || "";

  // Định nghĩa hàm getTagUserList để trả về danh sách user tag, có thêm mục @All
  const getTagUserList = React.useCallback(() => [
    {
      id: 0,
      userName: "All",
      name: "Báo cho cả nhóm",
      avatar: undefined,
      isAll: true,
    },
    ...tagUsers
  ], [tagUsers]);

  if (loading) {
    return (
      <Box style={{ minHeight: 200 }}>
        <Flex justify="center" align="center" py={50}>
          <Loader size="md" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box style={{
      height: "70vh",
      display: "flex",
      flexDirection: "column",
      minHeight: "400px"
    }}>
      {/* Comments List */}
      <ScrollArea 
        style={{ 
          flex: 1, 
          minHeight: 0,
          backgroundColor: "#f8f9fa", // Nền xám nhẹ
        }} 
        viewportRef={(viewport) => {
          // Auto-scroll to bottom when new comments are added
          if (viewport) {
            setTimeout(() => {
              viewport.scrollTop = viewport.scrollHeight;
            }, 100);
          }
        }}
      >
        <Box px={{ base: "xs", sm: "md" }} pt={{ base: "xs", sm: "md" }} pb="xs">
          {comments.length === 0 ? (
            <Text ta="center" c="dimmed" py={20} size="sm">
              Chưa có bình luận nào. Hãy là người đầu tiên bình luận!
            </Text>
          ) : (
            comments.map((comment, index) => {
              // Check if comment belongs to current user by comparing both username and userId
              const isOwnComment = 
                comment.username === currentUsername || 
                comment.createdById.toString() === currentUserId ||
                comment.username.toLowerCase() === currentUsername.toLowerCase();

              return (
                <Flex
                  key={comment.id}
                  justify={isOwnComment ? "flex-end" : "flex-start"}
                  mb={{ base: "xs", sm: "sm" }}
                >
                  <Box style={{ 
                    maxWidth: "85%", // Increase on mobile
                    "@media (min-width: 768px)": {
                      maxWidth: "75%"
                    }
                  }}>
                    <Group
                      justify="space-between"
                      mb="xs"
                      gap="xs"
                      wrap="nowrap"
                    >
                      {isOwnComment ? (
                        <>
                          <Group gap="xs" wrap="nowrap">
                            <Avatar
                              color="green"
                              radius="xl"
                              size="sm"
                              src={null}
                            >
                              {comment.username?.charAt(0)?.toUpperCase() || "U"}
                            </Avatar>
                            <Text fw={600} size="sm" c="dark.8">
                              Bạn
                            </Text>
                          </Group>
                          <Group gap="xs" wrap="nowrap">
                            <IconClock size={12} color="#868e96" />
                            <Text size="xs" c="dimmed">
                              {formatDateTransfer(comment.createdAt)}
                            </Text>
                            <Button
                              variant="subtle"
                              color="red"
                              size="xs"
                              p={4}
                              onClick={() => handleDeleteComment(comment.id)}
                              style={{ minWidth: "auto", height: "auto" }}
                            >
                              <IconTrash size={12} />
                            </Button>
                          </Group>
                        </>
                      ) : (
                        <>
                          <Group gap="xs" wrap="nowrap">
                            <Avatar
                              color="blue"
                              radius="xl"
                              size="sm"
                              src={null}
                            >
                              {comment.username?.charAt(0)?.toUpperCase() || "U"}
                            </Avatar>
                            <Text 
                              fw={600} 
                              size="sm" 
                              c="dark.8"
                              truncate
                              style={{ maxWidth: "120px" }}
                            >
                              {comment.username}
                            </Text>
                          </Group>
                          <Group gap="xs" wrap="nowrap">
                            <IconClock size={12} color="#868e96" />
                            <Text size="xs" c="dimmed">
                              {formatDateTransfer(comment.createdAt)}
                            </Text>
                          </Group>
                        </>
                      )}
                    </Group>
                    <Paper
                      radius="lg"
                      p="sm"
                      style={{
                        backgroundColor: isOwnComment ? "#e3f2fd" : "transparent", // Màu xanh nhẹ nhàng hơn
                        color: isOwnComment ? "#1565c0" : "inherit", // Chữ xanh đậm thay vì trắng
                        border: isOwnComment ? "1px solid #bbdefb" : "1px solid #e4e6ea", // Border xanh nhẹ
                        borderRadius: isOwnComment
                          ? "18px 18px 4px 18px"
                          : "18px 18px 18px 4px",
                      }}
                    >
                      <Text size="sm" style={{ lineHeight: "1.4" }}>
                        <div dangerouslySetInnerHTML={{ __html: renderCommentContent(comment.content) }} />
                      </Text>
                    </Paper>
                  </Box>
                </Flex>
              );
            })
          )}
        </Box>
      </ScrollArea>

      {/* Divider */}
      <Divider my="xs" />

      {/* Add Comment Form */}
      <Box px={{ base: "xs", sm: "md" }} pb={{ base: "xs", sm: "md" }} pt="xs" style={{ flexShrink: 0, position: "relative" }}>
        <Group align="flex-start" gap="sm">
          <Avatar
            color="green"
            radius="xl"
            size="sm"
            src={null}
          >
            {currentUsername?.charAt(0)?.toUpperCase() || "U"}
          </Avatar>
          <Box style={{ flex: 1, position: "relative" }}>
            <Box style={{ position: "relative" }}>
              {/* Div hiển thị HTML với @tag màu xanh */}
              
              <Textarea
                ref={textareaRef}
                placeholder="Viết bình luận..."
                value={displayComment}
                onChange={handleTextareaChange}
                minRows={2}
                maxRows={3}
                mb="xs"
                size="sm"
                styles={{
                  input: {
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                    border: "1px solid #e4e6ea",
                    fontSize: "14px",
                    padding: "8px 12px",
                    fontFamily: "inherit",
                    color: "black",
                    resize: "none",
                  },
                }}
              />
            </Box>
            
            {/* Tag user dropdown */}
            {showTagDropdown && getTagUserList().length > 0 && (
              <Paper
                shadow="md"
                style={{
                  position: "absolute",
                  zIndex: 9999,
                  bottom: textareaRef.current ? textareaRef.current.offsetHeight + 35 : 50,
                  left: 0,
                  minWidth: 220,
                  maxHeight: 260,
                  overflowY: "auto",
                  background: "#fff",
                  borderRadius: 8,
                  border: "1px solid #e4e6ea",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                }}
              >
                {getTagUserList()
                  .filter(u => {
                    const matchesSearch = u.isAll ||
                      u.name.toLowerCase().includes(tagSearch.toLowerCase()) ||
                      u.userName.toLowerCase().includes(tagSearch.toLowerCase());
                    
                    // Lọc bỏ user đã được tag - check bằng @(tên)
                    const notAlreadyTagged = u.isAll || !displayComment.includes(`@(${u.name})`);
                    
                    return matchesSearch && notAlreadyTagged;
                  })
                  .map(user => (
                    <Box
                      key={user.isAll ? "all" : user.id}
                      style={{
                        cursor: "pointer",
                        padding: "8px 16px",
                        background: user.isAll ? "#f5f7fa" : undefined,
                      }}
                      onMouseDown={e => {
                        e.preventDefault();
                        handleTagUserSelect(user);
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = user.isAll ? "#f5f7fa" : "transparent";
                      }}
                    >
                      <Group gap="xs" wrap="nowrap">
                        <Avatar radius="xl" size="md" src={proxyAvatar(user.avatar, 40)} color={user.isAll ? "blue" : undefined}>
                          {user.isAll ? <IconMessage size={20} /> : (user.name?.charAt(0)?.toUpperCase() || "U")}
                        </Avatar>
                        <Box style={{ flex: 1 }}>
                          <Text fw={500} size="sm">
                            {user.name}
                            {user.isAll && <Text span size="xs" c="blue" ml={6}>@All</Text>}
                          </Text>
                          {!user.isAll && (
                            <Text size="xs" c="dimmed">@{user.userName}</Text>
                          )}
                        </Box>
                      </Group>
                    </Box>
                  ))}
                {getTagUserList().filter(u => {
                  const matchesSearch = u.isAll ||
                    u.name.toLowerCase().includes(tagSearch.toLowerCase()) ||
                    u.userName.toLowerCase().includes(tagSearch.toLowerCase());
                  const notAlreadyTagged = u.isAll || !displayComment.includes(`@(${u.name})`);
                  return matchesSearch && notAlreadyTagged;
                }).length === 0 && (
                  <Text size="sm" c="dimmed" px={12} py={6}>Không tìm thấy người dùng</Text>
                )}
              </Paper>
            )}
            <Flex justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                onClick={() => {
                  setDisplayComment("");
                  setRawComment("");
                  setMentionedUsers([]);
                }}
                disabled={!displayComment.trim() || isSubmitting}
              >
                Hủy
              </Button>
              <Button
                leftSection={<IconSend size={12} />}
                color="blue"
                size="xs"
                onClick={addComment}
                loading={isSubmitting}
                disabled={!displayComment.trim()}
              >
                Gửi
              </Button>
            </Flex>
          </Box>
        </Group>
      </Box>
    </Box>
  );
};

const CommentModal = ({ documentId }: CommentModalProps) => {
  const openModalComments = () => {
    if (!documentId) {
      NotificationExtension.Info("Không có thông tin hồ sơ !");
      return;
    }

    modals.openConfirmModal({
      title: (
        <Group gap="xs">
          <IconMessage size={20} />
          <Title order={4}>Danh sách bình luận</Title>
        </Group>
      ),
      size: "lg",
      styles: {
        content: {
          maxHeight: "90vh",
          margin: window.innerWidth < 768 ? "10px" : "auto",
        },
        body: {
          padding: 0,
        },
        header: {
          padding: window.innerWidth < 768 ? "10px" : "16px",
        },
      },
      children: <CommentModalContent documentId={documentId} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  return { openModalComments };
};


export default CommentModal;
